import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import {
    BaseButton,
    ClaimResponsibilityStatus,
    forPhoneOnly,
    forTabletLandscapeDown,
    LayoutRow,
    Variables,
} from '@enter/core';
import { Icon } from '@mdi/react';
import { AlertBar, PageLayoutRow } from 'components';
import mdiChevronRight from '@mdi/svg/svg/chevron-right.svg';
import { Link } from 'react-router-dom';

export const headerActionButtonStyles = css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font: var(--label-md, 500 14px/1 Calibre, sans-serif);
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 0 14px;
    height: 36px;
    min-width: unset;
    cursor: pointer;
    transition:
        background 120ms var(--ease-out, ease-out),
        border-color 120ms var(--ease-out, ease-out);
    white-space: nowrap;
    filter: none;
`;

export const BaseLayoutRow = styled(LayoutRow)`
    align-items: stretch;
    padding: 0 48px;
    margin: 24px 0 0 0;
`;

export const Loader = styled.div`
    display: flex;
    flex-direction: column;
    flex: 0 1 40%;
    justify-content: center;

    & > svg {
        background-color: transparent;
    }
`;

export const StyledIcon = styled(Icon)`
    height: 16px;
    width: 16px;
    vertical-align: middle;
    cursor: pointer;
    fill: ${Variables.Colors.n70};
`;

export const ActionButtons = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const EditButton = styled(BaseButton)`
    ${headerActionButtonStyles}
`;

export const ComposeButton = styled(BaseButton)`
    ${headerActionButtonStyles}
`;

export const ComposeIcon = styled(Icon)`
    width: 16px;
    height: 16px;
    fill: currentColor;
`;
export const ComposeLabel = styled.span``;

export const MainHeader = styled.div`
    display: flex;
    align-items: center;
    padding-top: 16px;

    ${forPhoneOnly(`
        flex-direction: column;

        & > *:not(:first-of-type) {
            margin-top: 12px;
        }
    `)}
`;

export const MainGroup = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 16px;
`;

export const Title = styled.span`
    display: inline-flex;
    font-size: 20px;
    font-weight: 500;
    letter-spacing: 0.2px;
    color: ${Variables.Colors.p500};
    &:empty {
        display: none;
    }
`;

export const ResponsibilityStatus = ClaimResponsibilityStatus;

export const ResponsibilityCategory = styled.div`
    display: inline-flex;
    align-items: center;
`;

export const Seller = styled.div`
    padding: 6px 12px;
    margin-left: 8px;
    font-size: 14px;
    color: ${Variables.Colors.b300_l62};
    background-color: ${Variables.Colors.b300_l96};
    border-radius: ${Variables.Rounded.small};
    border: 1px solid ${Variables.Colors.b300_l90};
`;

export const Spacer = styled.span`
    flex-grow: 1;
    display: inline-block;
`;

export const SubHeader = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 16px;

    ${forPhoneOnly(`
        flex-direction: column;
    `)}
`;

export const StyledPageLayoutRow = styled(PageLayoutRow)`
    margin-top: 0;
    margin-bottom: 24px;
    flex-wrap: nowrap;
`;

export const OverviewLayoutRow = styled(PageLayoutRow)`
    flex-direction: row;
    margin-top: 0;
    margin-bottom: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 24px;

    ${forTabletLandscapeDown(`
       grid-template-columns: 1fr;
       grid-template-rows: auto auto;
    `)}
`;
export const OverviewLayoutColumn = styled.div`
    display: flex;
    flex: 1 1 calc(50% - 32px);
    flex-direction: column;
    margin-bottom: 24px;

    &:not(:first-of-type) {
        flex: 1 1 calc(50% - 32px);
        margin-right: 0px;
    }
    &:first-of-type > div:not(:last-child) {
        margin-bottom: 24px;
    }

    ${forTabletLandscapeDown(`
        flex: 1 1 100%;
        margin-right: 0;

        /* NB: use specific selector to remove for mobile */
        &:not(:first-of-type) {
            margin-left: 0;
            flex: 1 1 50%;
        }
    `)}
`;

export const StyledAlertBar = styled(AlertBar)`
    margin-right: 32px;
    margin-bottom: 24px;
`;

const breadcrumbChevronStyles = `
    display: inline-block;
    content: ' ';
    background: url(${mdiChevronRight});
    background-size: 24px auto;
    height: 24px;
    width: 24px;
`;

const claimBreadcrumbLinkStyles = `
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    letter-spacing: 0.2px;
    text-decoration: none;
`;

export const ClaimNumber = styled.span`
    margin-left: 10px;
    font-size: 12px;
`;

export const ClaimMetaLabel = styled.span`
    font-size: 14px;
    color: var(--text-x-mild, ${Variables.ThemeLight.TextXMild});
`;

export const ClaimMetaValue = styled.span`
    font-size: 14px;
    color: var(--text-medium, ${Variables.ThemeLight.TextMedium});
`;

export const ClaimBreadcrumb = styled.div`
    display: flex;
    align-items: center;

    & > a:first-of-type:not(:only-of-type)::after {
        ${breadcrumbChevronStyles}
    }
`;

export const ClaimBreadcrumbClaimsLink = styled(Link)`
    ${claimBreadcrumbLinkStyles}
    color: var(--primary-strong, ${Variables.Colors.p500});
    font-weight: 500;
`;

export const ClaimBreadcrumbPatientLink = styled(Link)`
    ${claimBreadcrumbLinkStyles}
    color: var(--text-strong, ${Variables.ThemeLight.TextStrong});
    font-weight: 600;
    gap: 4px;
`;

export const BreadcrumbOpenIcon = styled(Icon)`
    color: var(--text-x-mild, ${Variables.ThemeLight.TextXMild});
    width: 13px;
    height: 13px;
    flex-shrink: 0;
`;

export const BadgeWrapper = styled.div`
    display: inline-flex;
    align-items: center;
`;

export const Alert = styled.div<{ type?: 'success' }>`
    display: flex;
    justify-content: space-between;
    padding: 8px 48px;
    font-size: 18px;
    align-items: center;
    color: ${Variables.ThemeLight.TextMedium};
    background-color: ${props =>
        props.type === 'success' ? Variables.ThemeLight.SuccessTranslated3 : Variables.ThemeLight.WarningTranslated3};
    margin: -48px -48px 24px -48px;
`;

export const AlertContainer = styled.div`
    display: flex;
    align-items: center;
`;

export const HaltedAlertContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;
export const AlertTitle = styled.div`
    font-size: 16px;
    white-space: pre-line;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextMedium};
`;
export const AlertDescription = styled.div`
    font-size: 16px;
    white-space: pre-line;
    font-weight: 400;
    color: ${Variables.ThemeLight.TextMedium};
`;
export const AlertButton = styled(BaseButton)`
    color: ${Variables.ThemeLight.PrimaryConstant11};
    background-color: ${Variables.ThemeLight.PrimaryConstant6};
    border: 1px solid ${Variables.ThemeLight.NeutralOpacity5};
    border-radius: 6px;
`;

export const TalkToClaimIcon = styled(Icon)`
    width: 16px;
    height: 16px;
    fill: currentColor;
`;

export const CustomAIButton = styled(BaseButton)`
    ${headerActionButtonStyles}
    border: 1px solid var(--primary-strong, ${Variables.ThemeLight.PrimaryConstant6});
    background-color: transparent;
    color: var(--primary-strong, ${Variables.ThemeLight.PrimaryConstant6});

    &:hover:not(:disabled) {
        background-color: var(--primary-x-mild, ${Variables.ThemeLight.PrimaryMild2});
    }

    &:active:not(:disabled) {
        background-color: var(--primary-mild, ${Variables.ThemeLight.PrimaryMild1});
    }

    &:disabled {
        color: var(--text-x-mild, ${Variables.ThemeLight.TextXMild});
        border-color: var(--border, ${Variables.ThemeLight.Border1});
        cursor: not-allowed;
        filter: none;
        background-color: transparent;
    }
`;
export const AskButtonWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: visible;

    & > * {
        position: relative;
        z-index: 1;
    }

    &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 8px;
        opacity: 0;
        filter: blur(16px);
        background: radial-gradient(
            circle at center,
            ${Variables.ThemeLight.SuccessInverted9},
            ${Variables.ThemeLight.PrimaryInverted9},
            ${Variables.ThemeLight.WarningInverted9},
            ${Variables.ThemeLight.AttentionInverted9}
        );
        pointer-events: none;
        transition: opacity 0.2s ease;
        z-index: 0;
    }

    &:hover::before {
        opacity: 0.5;
    }

    &:active::before {
        opacity: 0.6;
    }
`;
